import { z } from 'zod';
import { moneySchema } from '../money';
import { cutLengthMmSchema } from '../inventory/cut-variant';
import { KIT_MAX_DEPTH } from '../inventory/kit';
import { incotermSchema } from '../documents/incoterms';

/** Belegart / Status (Modul sales, ADR 0007). */
export const salesDocumentKindSchema = z.enum([
  'QUOTE',
  'ORDER',
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'DELIVERY_NOTE',
  'PRO_FORMA',
  'CREDIT_NOTE',
  'PICKING_LIST',
]);
export type SalesDocumentKind = z.infer<typeof salesDocumentKindSchema>;

/**
 * Belegarten mit eigenem Nummernkreis je Gesellschaft (ADR 0029/0059) — für
 * sie muss ein **Absender** (ausstellende Gesellschaft) feststehen, weil er die
 * Belegnummer bestimmt.
 *
 * Bewusst hier und nicht je App: Die Liste stand doppelt (API-Mapper und
 * Anlege-Dialog) und lief auseinander — im Dialog fehlte `QUOTE`, weshalb das
 * Absender-Feld beim Angebot unsichtbar war und die Ableitung unbemerkt lief
 * (Befund 2026-08-05). Eine Quelle für beide Seiten.
 */
export const ENTITY_BOUND_SALES_KINDS: readonly SalesDocumentKind[] = [
  'QUOTE',
  'ORDER',
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'DELIVERY_NOTE',
  'CREDIT_NOTE',
  'PRO_FORMA',
];

const ENTITY_BOUND_SALES_KIND_SET: ReadonlySet<SalesDocumentKind> = new Set(ENTITY_BOUND_SALES_KINDS);

/** Braucht diese Belegart einen Absender (eigener Nummernkreis je Gesellschaft)? */
export function isEntityBoundSalesKind(kind: SalesDocumentKind): boolean {
  return ENTITY_BOUND_SALES_KIND_SET.has(kind);
}

/** Bestehende Belege eines Kunden auf dessen betreuende Gesellschaft umstellen (ADR 0029). */
export const reassignLegalEntitySchema = z.object({ customerId: z.string().uuid() });
export type ReassignLegalEntityInput = z.infer<typeof reassignLegalEntitySchema>;

/** Eingabe fürs Umwandeln eines Belegs in eine Zielart. */
export const convertSalesDocumentSchema = z.object({ targetKind: salesDocumentKindSchema });
export type ConvertSalesDocumentInput = z.infer<typeof convertSalesDocumentSchema>;

/**
 * Absender (ausstellende Gesellschaft) wechseln über Storno + Neuanlage — der
 * Weg, wenn ein Umnummerieren per PATCH nicht mehr zulässig ist: Beleg
 * festgeschrieben, oder die alte Nummer ist im Kreis der bisherigen Gesellschaft
 * nicht mehr die zuletzt vergebene. `null` = keine Gesellschaft.
 */
export const changeLegalEntitySchema = z.object({ legalEntityId: z.string().uuid().nullable() });
export type ChangeLegalEntityInput = z.infer<typeof changeLegalEntitySchema>;

/**
 * Eine VERSENDETE Rechnung wieder in den Entwurf holen, um sie zu korrigieren
 * (Tech-Lead 2026-08-05). Bewusst eine eigene Aktion statt eines stillen
 * Statuswechsels im Dropdown: das Festschreiben ist die Regel, das Öffnen die
 * begründete Ausnahme — die Begründung landet im Audit-Log.
 */
export const reopenSalesDocumentSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(5, 'Bitte kurz begründen, warum die Rechnung geöffnet wird (mind. 5 Zeichen).')
    .max(500),
});
export type ReopenSalesDocumentInput = z.infer<typeof reopenSalesDocumentSchema>;

/**
 * Belegarten, die sich wieder öffnen lassen — die rechnungsartigen. Für Angebot
 * und Auftrag ist der Rückweg SENT→DRAFT ohnehin frei (`manualStatusTargetsFor`),
 * für Lieferscheine bewusst gesperrt (der Warenausgang ist gebucht).
 */
export const REOPENABLE_SALES_KINDS: readonly SalesDocumentKind[] = [
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'CREDIT_NOTE',
];

/**
 * Kann dieser Beleg (nach Belegart und Status) grundsätzlich wieder geöffnet
 * werden? Spiegel der Server-Guards für die UI — ob zusätzlich eine Gutschrift
 * existiert, weiß nur der Server (`reopenDocument`).
 */
export function canReopenSalesDocument(doc: {
  kind: SalesDocumentKind;
  status: SalesDocumentStatus;
}): boolean {
  return REOPENABLE_SALES_KINDS.includes(doc.kind) && doc.status === 'SENT';
}


export const salesDocumentStatusSchema = z.enum([
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'REJECTED',
  'PAID',
  'CANCELLED',
]);
export type SalesDocumentStatus = z.infer<typeof salesDocumentStatusSchema>;

/**
 * Vier-Augen-Freigabezustand eines Verkaufsbelegs (ADR 0065) — separate
 * Dimension neben `status`. NONE = keine Freigabe angefordert, PENDING = wartet,
 * RELEASED = freigegeben (Versand entsperrt), REJECTED = abgelehnt (zurück im Entwurf).
 */
export const salesReleaseStateSchema = z.enum(['NONE', 'PENDING', 'RELEASED', 'REJECTED']);
export type SalesReleaseState = z.infer<typeof salesReleaseStateSchema>;

/**
 * Ereignis der Freigabe-Historie (ADR 0065, Nachtrag 2026-07-28). Die Historie ist
 * append-only und bleibt erhalten, wenn eine Freigabe erlischt — vorher wurde sie aus
 * Skalarfeldern gerendert, die beim Bearbeiten geleert wurden.
 *
 * `CHANGED` = der Beleg wurde inhaltlich geändert; `changedGroups` benennt die betroffenen
 * Feldgruppen, `releaseLapsed` sagt, ob dadurch eine bestehende Freigabe erloschen ist.
 */
export const salesReleaseEventTypeSchema = z.enum([
  'REQUESTED',
  'RELEASED',
  'REJECTED',
  'CHANGED',
  'WITHDRAWN',
]);
export type SalesReleaseEventType = z.infer<typeof salesReleaseEventTypeSchema>;

export const salesReleaseEventSchema = z.object({
  id: z.string().uuid(),
  type: salesReleaseEventTypeSchema,
  /** Wer es ausgelöst hat; null nur bei übernommenen Altdaten. */
  actorId: z.string().uuid().nullable(),
  /** Bei REQUESTED die gewählten Freigeber. */
  approverIds: z.array(z.string().uuid()),
  /** Bei CHANGED die geänderten Feldgruppen („Positionen", „Termine" …). */
  changedGroups: z.array(z.string()),
  /** Bei REJECTED der Ablehnungsgrund. */
  reason: z.string().nullable(),
  /** Bei CHANGED: hat die Änderung eine bestehende Freigabe erlöschen lassen? */
  releaseLapsed: z.boolean(),
  createdAt: z.string().datetime(),
});
export type SalesReleaseEventResponse = z.infer<typeof salesReleaseEventSchema>;

/**
 * Erlaubte Status je Belegart (#29). Ein Angebot kennt weder „Bezahlt" noch
 * „Storniert", eine Rechnung dagegen keine Angebots-Status wie „Angenommen".
 * Backend (updateDocument) und Frontend (Statuswahl in Belegliste/Editor) teilen
 * sich diese Zuordnung, damit UI und Validierung nicht auseinanderlaufen.
 */
export const ALLOWED_STATUSES_BY_KIND: Record<SalesDocumentKind, SalesDocumentStatus[]> = {
  QUOTE: ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED'],
  PRO_FORMA: ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED'],
  ORDER: ['DRAFT', 'SENT', 'CANCELLED'],
  PICKING_LIST: ['DRAFT', 'SENT', 'CANCELLED'],
  DELIVERY_NOTE: ['DRAFT', 'SENT', 'CANCELLED'],
  INVOICE: ['DRAFT', 'SENT', 'PAID', 'CANCELLED'],
  ADVANCE_INVOICE: ['DRAFT', 'SENT', 'PAID', 'CANCELLED'],
  FINAL_INVOICE: ['DRAFT', 'SENT', 'PAID', 'CANCELLED'],
  CREDIT_NOTE: ['DRAFT', 'SENT', 'PAID', 'CANCELLED'],
};

/**
 * Status, die ausschließlich das System setzt (nicht manuell über UI/HTTP):
 * „Bezahlt" entsteht automatisch aus dem Zahlungseingang (Modul Buchhaltung).
 */
export const SYSTEM_MANAGED_STATUSES: readonly SalesDocumentStatus[] = ['PAID'];

/**
 * Terminale Status: einmal erreicht, gibt es keinen manuellen Weg mehr heraus.
 * „Bezahlt" verlässt nur die Buchhaltung über den Zahlungsstand (interner
 * System-Pfad), „Storniert" ist endgültig — Korrektur läuft über Gutschrift/
 * Neubeleg, nicht über das Zurückdrehen des Status. Backend (updateDocument)
 * und Frontend (Status-Menü) teilen sich diese Festlegung.
 */
export const TERMINAL_SALES_STATUSES: readonly SalesDocumentStatus[] = ['PAID', 'CANCELLED'];

/** Ist dieser Status terminal (manuell nicht mehr verlassbar)? */
export function isTerminalSalesStatus(status: SalesDocumentStatus): boolean {
  return TERMINAL_SALES_STATUSES.includes(status);
}

/** Darf eine Belegart diesen Status überhaupt tragen? */
export function isStatusAllowedForKind(
  kind: SalesDocumentKind,
  status: SalesDocumentStatus,
): boolean {
  return ALLOWED_STATUSES_BY_KIND[kind].includes(status);
}

/** Wird dieser Status ausschließlich systemseitig gesetzt (nicht manuell)? */
export function isSystemManagedStatus(status: SalesDocumentStatus): boolean {
  return SYSTEM_MANAGED_STATUSES.includes(status);
}

/**
 * Manuell (über UI/HTTP) wählbare Status einer Belegart: die erlaubten Status
 * ohne die systemgesteuerten (z. B. „Bezahlt"). Grundlage für das Status-Menü
 * in der Belegliste und im Editor.
 */
export function manualStatusesForKind(kind: SalesDocumentKind): SalesDocumentStatus[] {
  return ALLOWED_STATUSES_BY_KIND[kind].filter((status) => !isSystemManagedStatus(status));
}

/** Belegarten, die nach dem Festschreiben (SENT) unveränderlich sind (Rechnungswesen). */
const IMMUTABLE_AFTER_SENT_KINDS: readonly SalesDocumentKind[] = [
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'CREDIT_NOTE',
];

/**
 * Darf dieser Statusübergang MANUELL (UI/HTTP-PATCH) ausgeführt werden?
 * Ergänzt die Mengen-Prüfung (`isStatusAllowedForKind`) und die Terminal-Regel
 * um einen Übergangsgraphen — vorher war jeder erlaubte Status von jedem
 * nicht-terminalen Status aus erreichbar (Audit 2026-08-05):
 *
 * - Rechnungsartige Belege verlassen SENT nie zurück nach DRAFT — sonst wäre
 *   die dokumentierte Unveränderlichkeit festgeschriebener Rechnungen per
 *   Statusmenü aushebelbar (Positionen/Steuersatz wieder änderbar, Beleg
 *   löschbar).
 * - Der Lieferschein wechselt DRAFT→SENT nur über den Warenausgang (`ship()`,
 *   bucht die Lagerabgänge) und SENT→DRAFT gar nicht (ein erneutes `ship()`
 *   würde alle Abgänge ein zweites Mal buchen).
 *
 * Backend (updateDocument) und Frontend (Status-Menü) teilen sich diese Regel,
 * damit das Menü nichts anbietet, was der Server ablehnt.
 */
export function isManualStatusTransitionAllowed(
  kind: SalesDocumentKind,
  from: SalesDocumentStatus,
  to: SalesDocumentStatus,
): boolean {
  if (from === to) return true;
  if (IMMUTABLE_AFTER_SENT_KINDS.includes(kind) && from === 'SENT' && to === 'DRAFT') return false;
  if (kind === 'DELIVERY_NOTE' && to === 'DRAFT' && from === 'SENT') return false;
  if (kind === 'DELIVERY_NOTE' && from === 'DRAFT' && to === 'SENT') return false;
  return true;
}

/**
 * Manuell erreichbare Ziel-Status aus dem aktuellen Status heraus — die
 * Grundlage des Status-Menüs (statt `manualStatusesForKind`, das den
 * IST-Zustand ignorierte und z. B. einer versendeten Rechnung „Entwurf"
 * anbot, den der Server ablehnt).
 */
export function manualStatusTargetsFor(
  kind: SalesDocumentKind,
  from: SalesDocumentStatus,
): SalesDocumentStatus[] {
  if (isTerminalSalesStatus(from)) return [from];
  return manualStatusesForKind(kind).filter(
    (to) => to === from || isManualStatusTransitionAllowed(kind, from, to),
  );
}

/**
 * Belegarten, die sich duplizieren lassen (Tech-Lead 2026-07-28). Nicht dabei sind
 * die ABGELEITETEN Arten: Anzahlungs- und Schlussrechnung entstehen aus dem Auftrag
 * (nur dort werden die gestellten Anzahlungen verrechnet), die Stornorechnung durch
 * das Stornieren einer Rechnung, die Kommissionierliste stammt aus Altdaten. Eine
 * freistehende Kopie davon wäre ein Beleg ohne Vorgang — dieselbe Begründung, aus
 * der `createDocument` freistehende Schlussrechnungen ablehnt.
 *
 * Backend (`duplicateDocument`) und Frontend (Zeilenmenü der Belegliste) teilen
 * sich diese Liste, damit die UI nichts anbietet, was der Server ablehnt.
 */
export const DUPLICABLE_SALES_KINDS: readonly SalesDocumentKind[] = [
  'QUOTE',
  'ORDER',
  'DELIVERY_NOTE',
  'INVOICE',
  'PRO_FORMA',
];

/** Lässt sich diese Belegart duplizieren? */
export function canDuplicateSalesKind(kind: SalesDocumentKind): boolean {
  return DUPLICABLE_SALES_KINDS.includes(kind);
}

/** Belegsprache (PDF-Übersetzung DE/EN). */
export const salesDocumentLanguageSchema = z.enum(['DE', 'EN']);
export type SalesDocumentLanguage = z.infer<typeof salesDocumentLanguageSchema>;

/** Eingabe einer Position. `longText` wird beim Artikel-Pick aus dem Stamm vorbefüllt. */
export const salesItemInputSchema = z.object({
  articleId: z.string().uuid().optional(),
  /**
   * Frei erfasste/importierte Artikelnummer OHNE Katalog-Verknüpfung.
   * Wird nur verwendet, wenn keine `articleId` gesetzt ist (sonst gewinnt der Katalog-Snapshot).
   */
  articleNumber: z.string().trim().max(60).nullable().optional(),
  /** Positionstyp: Artikel (mit Menge/Preis), Text, Überschrift oder Seitenumbruch. */
  positionType: z.enum(['ARTICLE', 'TEXT', 'HEADING', 'PAGE_BREAK']).optional(),
  description: z.string().trim().min(1).max(500),
  longText: z.string().trim().max(5000).optional(),
  /**
   * Baugruppe für Gruppenüberschriften im Lieferschein-Druck (ADR 0043 §3).
   * Wird beim Wandeln Kommissionierauftrag → Lieferschein aus `PickingItem.assemblyGroup`
   * übernommen; bei anderen Entstehungswegen bleibt sie leer (kein Editor-Pflichtfeld).
   */
  assemblyGroup: z.string().trim().max(200).optional(),
  unit: z.string().trim().max(20).optional(),
  // Menge darf negativ sein: Gutschriften, Rabatt-/Abzugszeilen und der
  // Anzahlungsabzug der Schlussrechnung brauchen negative Mengen bzw. Preise.
  // 0 bleibt für Text-/Überschriftszeilen erlaubt (kein Mengen-/Preisbezug).
  quantity: z.number().finite(),
  // Einzelpreis darf ebenfalls negativ sein (moneySchema erlaubt `-`), z. B. für
  // Anzahlungsabzüge (unitPriceAmount = −Anzahlung) und Gutschriftszeilen.
  unitPrice: moneySchema,
  /** Positionsrabatt in % (0–100); null/weglassen = kein Rabatt. */
  discountPercent: z.number().min(0).max(100).nullable().optional(),
  /** Artikelnummer NICHT aufs PDF drucken (Wert-/Textpositionen wie Anzahlung/Abzug). */
  hideArticleNumber: z.boolean().optional(),
  /** Ablängung: geschnittene Länge in mm (Positions-Parameter, ADR 0040). */
  cutLengthMm: cutLengthMmSchema.nullable().optional(),
  /** Gedruckter Nummer-Anhang, der unter der Artikelnummer erscheint (z. B. „L = 2300 mm"). */
  articleNumberSuffix: z.string().trim().max(40).nullable().optional(),
  /**
   * Zusatzmaterial-Kennzeichnung (VB-26016, ADR 0088) — wie beim Materialbedarf:
   * Artikelnummer des Hauptartikels als **Snapshot**, KEIN Fremdschlüssel. Die
   * Position ist ab dem Einfügen unabhängig (frei editier- und löschbar) und muss
   * die Herkunft auch dann noch tragen, wenn der Hauptartikel aus dem Beleg
   * verschwindet. null/weglassen = normale Position.
   */
  kitParentNumber: z.string().trim().max(60).nullable().optional(),
  /** Einrücktiefe: 0 = normale Position, 1 = direktes Zusatzmaterial, 2 = dessen Zusatzmaterial … */
  kitDepth: z.number().int().min(0).max(KIT_MAX_DEPTH).optional(),
  /** Sortier-Position (0-basiert). Steuert die Reihenfolge der Positionen (Drag & Drop). */
  position: z.number().int().nonnegative().optional(),
});
export type SalesItemInput = z.infer<typeof salesItemInputSchema>;
export const updateSalesItemSchema = salesItemInputSchema.partial();
export type UpdateSalesItemInput = z.infer<typeof updateSalesItemSchema>;

/**
 * Eine Zeile beim **Ersetzen aller Positionen** (`PUT …/items`). Wie
 * {@link salesItemInputSchema}, mit zwei Unterschieden:
 *
 * - `id` gesetzt ⇒ bestehende Position (wird aktualisiert), `id` fehlt ⇒ neue Position.
 * - **kein `position`**: Die Reihenfolge im Array IST die Reihenfolge im Beleg. Zwei
 *   konkurrierende Quellen für die Reihenfolge (Index UND Feld) wären eine Einladung
 *   zum Widerspruch — der Editor schickt ohnehin immer die vollständige Liste.
 */
export const replaceSalesItemSchema = salesItemInputSchema
  .omit({ position: true })
  .extend({ id: z.string().uuid().optional() });
export type ReplaceSalesItemInput = z.infer<typeof replaceSalesItemSchema>;

/**
 * Vollständige Positionsliste eines Belegs. Was hier fehlt, wird gelöscht — der
 * Aufrufer schickt den Soll-Zustand, nicht eine Folge von Änderungen.
 *
 * Die Obergrenze ist großzügig (Anlagenbau-Angebote haben schon mal 200 Zeilen),
 * begrenzt aber die Transaktionsdauer nach oben.
 */
export const replaceSalesItemsSchema = z.object({
  items: z.array(replaceSalesItemSchema).max(500),
});
export type ReplaceSalesItemsInput = z.infer<typeof replaceSalesItemsSchema>;

/** Beleg anlegen (Positionen folgen separat). */
export const createSalesDocumentSchema = z.object({
  kind: salesDocumentKindSchema,
  customerId: z.string().uuid(),
  /** Ausstellende Gesellschaft (Override). Ohne Angabe wird sie aus dem Kunden abgeleitet. */
  legalEntityId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  /** Ohne Angabe wird die Kundensprache übernommen (Fallback DE). */
  language: salesDocumentLanguageSchema.optional(),
  currency: z.enum(['EUR', 'USD', 'CHF']).default('EUR'),
  /** Ohne Angabe wird der Satz serverseitig ermittelt (Inland/Reverse Charge/Ausfuhr). */
  taxRate: z.number().min(0).max(100).optional(),
  documentDate: z.string().datetime().optional(),
  validUntil: z.string().datetime().optional(),
  dueDate: z.string().datetime().optional(),
  notes: z.string().trim().max(5000).optional(),
  /** Referenz des Kunden (Bestellnummer, „Ihr Zeichen"). */
  customerReference: z.string().trim().max(120).optional(),
  /** Interne Referenz / Sachbearbeiter („Unser Zeichen") — Druck-Text. */
  internalReference: z.string().trim().max(120).optional(),
  /** Sachbearbeiter als User-Verknüpfung (Soft-Ref); ohne Angabe der anlegende User. */
  assigneeUserId: z.string().uuid().optional(),
  /** Abweichende Rechnungsadresse (Freitext, mehrzeilig). */
  invoiceAddress: z.string().trim().max(500).optional(),
  /** Abweichende Lieferadresse (Freitext, mehrzeilig). */
  deliveryAddress: z.string().trim().max(500).optional(),
  /** Zahlungsziel in Tagen (ohne Angabe aus dem Kundenstamm). */
  paymentTermsDays: z.number().int().min(0).max(365).optional(),
  /** Skonto in % (null/weglassen = kein Skonto). */
  cashDiscountPercent: z.number().min(0).max(100).optional(),
  /** Skonto-Frist in Tagen ab Belegdatum. */
  cashDiscountDays: z.number().int().min(0).max(365).optional(),
  /** Steuerfreie Rechnung (USt 0 %) — taxRate wird serverseitig auf 0 gesetzt. */
  taxExempt: z.boolean().optional(),
  /** Grund der Steuerbefreiung (PDF-Hinweis), z. B. Reverse Charge. */
  taxExemptReason: z.string().trim().max(300).optional(),
  /** Lieferangaben (Beleg-Kopf). */
  deliveryDate: z.string().datetime().optional(),
  deliveryTimeText: z.string().trim().max(200).optional(),
  /** Lieferbedingung (Incoterms 2020) — ersetzt die frühere Versandart. */
  incoterm: incotermSchema.optional(),
  /** Erfüllungsort / benannter Ort zum Incoterm (z. B. „Kläranlage Wien"). */
  incotermLocation: z.string().trim().max(200).optional(),
  shippingWeightKg: z.number().min(0).max(1_000_000).optional(),
  referenceText: z.string().trim().max(500).optional(),
});
export type CreateSalesDocumentInput = z.infer<typeof createSalesDocumentSchema>;

/** Beleg-Kopf aktualisieren. Der Kunde ist nur im Entwurf wechselbar (Re-Snapshot). */
export const updateSalesDocumentSchema = z.object({
  status: salesDocumentStatusSchema.optional(),
  /** Kunde nachträglich wechseln (nur Entwurf) — Name/Anschrift/Nr./USt-IdNr. werden neu übernommen. */
  customerId: z.string().uuid().optional(),
  language: salesDocumentLanguageSchema.optional(),
  taxRate: z.number().min(0).max(100).optional(),
  documentDate: z.string().datetime().optional(),
  validUntil: z.string().datetime().nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  customerReference: z.string().trim().max(120).nullable().optional(),
  internalReference: z.string().trim().max(120).nullable().optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  customerNumber: z.string().trim().max(120).nullable().optional(),
  customerVatId: z.string().trim().max(120).nullable().optional(),
  invoiceAddress: z.string().trim().max(500).nullable().optional(),
  deliveryAddress: z.string().trim().max(500).nullable().optional(),
  /** Versender / ausstellende Gesellschaft (nachträglich änderbar; keine Neunummerierung). */
  legalEntityId: z.string().uuid().nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(365).nullable().optional(),
  cashDiscountPercent: z.number().min(0).max(100).nullable().optional(),
  cashDiscountDays: z.number().int().min(0).max(365).nullable().optional(),
  taxExempt: z.boolean().optional(),
  taxExemptReason: z.string().trim().max(300).nullable().optional(),
  deliveryDate: z.string().datetime().nullable().optional(),
  deliveryTimeText: z.string().trim().max(200).nullable().optional(),
  incoterm: incotermSchema.nullable().optional(),
  incotermLocation: z.string().trim().max(200).nullable().optional(),
  shippingWeightKg: z.number().min(0).max(1_000_000).nullable().optional(),
  /** Manuelle Gewichtsangabe; null = automatisch aus Artikelgewichten berechnet. */
  shippingWeightKgManual: z.number().min(0).max(1_000_000).nullable().optional(),
  referenceText: z.string().trim().max(500).nullable().optional(),
  /** Feld-Keys, die NICHT aufs PDF gedruckt werden (siehe SALES_PDF_FIELDS). */
  pdfHiddenFields: z.array(z.string().trim().max(60)).max(50).optional(),
});
export type UpdateSalesDocumentInput = z.infer<typeof updateSalesDocumentSchema>;

/**
 * Optionale Beleg-Felder, deren PDF-Druck pro Beleg abschaltbar ist. Die Keys
 * bilden den Vertrag zwischen Editor-UI (Auswahl) und PDF-Renderer (Ausblenden);
 * `pdfHiddenFields` speichert die abgewählten Keys. Pflichtinhalte (Empfänger,
 * Belegdatum, Positionen, Summen) sind bewusst NICHT abschaltbar.
 */
export const SALES_PDF_FIELDS = [
  { key: 'customerNumber', label: 'Kundennummer' },
  { key: 'customerVatId', label: 'USt-IdNr. des Kunden' },
  { key: 'validUntil', label: 'Gültig bis' },
  { key: 'dueDate', label: 'Fällig am' },
  { key: 'internalReference', label: 'Sachbearbeiter / Unser Zeichen' },
  { key: 'referenceText', label: 'Bezugstext' },
  { key: 'projectCode', label: 'Projekt' },
  { key: 'deliveryDate', label: 'Liefertermin' },
  { key: 'deliveryTimeText', label: 'Lieferzeit' },
  { key: 'incoterm', label: 'Incoterm / Erfüllungsort' },
  { key: 'shippingWeightKg', label: 'Gewicht' },
  { key: 'paymentTerms', label: 'Zahlungstext (Fälligkeit)' },
  { key: 'cashDiscount', label: 'Skonto-Hinweis' },
  { key: 'notes', label: 'Notiz / Zahlungsbedingungen' },
] as const;
export type SalesPdfFieldKey = (typeof SALES_PDF_FIELDS)[number]['key'];

export const salesItemResponseSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  position: z.number().int(),
  positionType: z.enum(['ARTICLE', 'TEXT', 'HEADING', 'PAGE_BREAK']),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  longText: z.string().nullable(),
  /** Baugruppe für Gruppenüberschriften im Lieferschein-Druck (null = keine, ADR 0043 §3). */
  assemblyGroup: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number(),
  unitPrice: moneySchema,
  /** Positionsrabatt in % (null = kein Rabatt). lineNet ist bereits rabattiert. */
  discountPercent: z.number().nullable(),
  /** Artikelnummer NICHT aufs PDF drucken (default false). */
  hideArticleNumber: z.boolean(),
  /** Ablängung: geschnittene Länge in mm (null = keine, ADR 0040). */
  cutLengthMm: z.number().int().nullable(),
  /** Gedruckter Nummer-Anhang unter der Artikelnummer (null = keiner). */
  articleNumberSuffix: z.string().nullable(),
  /**
   * Artikelnummer des Hauptartikels, aus dessen Zusatzmaterial diese Position
   * entstanden ist (null = normale Position, ADR 0088). Nur Kennzeichnung —
   * die Position selbst ist unabhängig.
   */
  kitParentNumber: z.string().nullable(),
  /** Einrücktiefe der Kennzeichnung (0 = normale Position, 1 = direktes Zusatzmaterial). */
  kitDepth: z.number().int().min(0),
  lineNet: moneySchema,
});
export type SalesItemResponse = z.infer<typeof salesItemResponseSchema>;

export const salesDocumentResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  kind: salesDocumentKindSchema,
  number: z.string(),
  status: salesDocumentStatusSchema,
  customerId: z.string().uuid(),
  customerName: z.string(),
  customerAddress: z.string().nullable(),
  customerNumber: z.string().nullable(),
  customerVatId: z.string().nullable(),
  invoiceAddress: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  /** Verknüpfte Intercompany-Bestellung der empfangenden Gesellschaft (Phase 4). */
  intercompanyPurchaseOrderId: z.string().uuid().nullable(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  language: salesDocumentLanguageSchema,
  currency: z.enum(['EUR', 'USD', 'CHF']),
  taxRate: z.number(),
  net: moneySchema,
  tax: moneySchema,
  gross: moneySchema,
  documentDate: z.string().datetime(),
  validUntil: z.string().datetime().nullable(),
  dueDate: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  customerReference: z.string().nullable(),
  internalReference: z.string().nullable(),
  /**
   * Überschreibt die Belegart-Überschrift im PDF („1. Teilrechnung" statt
   * „Rechnung"). Wird beim Fakturieren einer Zahlungsplan-Position als Snapshot
   * gesetzt; null = normale Überschrift. Nur lesend — kein Eingabefeld.
   */
  titleOverride: z.string().nullable(),
  assigneeUserId: z.string().uuid().nullable(),
  paymentTermsDays: z.number().int().nullable(),
  cashDiscountPercent: z.number().nullable(),
  cashDiscountDays: z.number().int().nullable(),
  taxExempt: z.boolean(),
  taxExemptReason: z.string().nullable(),
  deliveryDate: z.string().datetime().nullable(),
  deliveryTimeText: z.string().nullable(),
  incoterm: incotermSchema.nullable(),
  incotermLocation: z.string().nullable(),
  shippingWeightKg: z.number().nullable(),
  /** Manuelle Gewichtsangabe (überschreibt die Berechnung); null = automatisch. */
  shippingWeightKgManual: z.number().nullable(),
  referenceText: z.string().nullable(),
  /** Feld-Keys, die NICHT aufs PDF gedruckt werden (siehe SALES_PDF_FIELDS). */
  pdfHiddenFields: z.array(z.string()),
  sourceDocumentId: z.string().uuid().nullable(),
  // Vier-Augen-Freigabe (ADR 0065).
  releaseState: salesReleaseStateSchema,
  /** Vom Ersteller gewählte Freigeber (User-IDs); EINE Freigabe daraus genügt. */
  releaseApproverIds: z.array(z.string().uuid()),
  releaseRequestedBy: z.string().uuid().nullable(),
  releaseRequestedAt: z.string().datetime().nullable(),
  releasedBy: z.string().uuid().nullable(),
  releasedAt: z.string().datetime().nullable(),
  releaseRejectedBy: z.string().uuid().nullable(),
  releaseRejectedAt: z.string().datetime().nullable(),
  releaseRejectionReason: z.string().nullable(),
  /**
   * Vollständige Freigabe-Historie, älteste zuerst (ADR 0065, Nachtrag 2026-07-28).
   * Überlebt das Erlöschen einer Freigabe — die Skalarfelder oben zeigen nur den
   * AKTUELLEN Stand, diese Liste die Abfolge.
   */
  releaseEvents: z.array(salesReleaseEventSchema),
  /** Ersteller des Belegs (für Vier-Augen: Ersteller ≠ Freigeber). */
  createdBy: z.string().uuid(),
  items: z.array(salesItemResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SalesDocumentResponse = z.infer<typeof salesDocumentResponseSchema>;

/**
 * Freigabe anfordern (Vier-Augen, ADR 0065): der Ersteller wählt eine oder
 * mehrere Personen als Freigeber. EINE Freigabe daraus genügt.
 */
export const requestSalesReleaseSchema = z.object({
  approverUserIds: z.array(z.string().uuid()).min(1).max(20),
});
export type RequestSalesReleaseInput = z.infer<typeof requestSalesReleaseSchema>;

/** Freigabe ablehnen — Grund ist Pflicht (wird dem Ersteller angezeigt). */
export const rejectSalesReleaseSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectSalesReleaseInput = z.infer<typeof rejectSalesReleaseSchema>;
